/**
 * Piping & Insulation Discipline Configuration
 * 
 * This configuration defines all aspects of the Piping matrix:
 * - Column definitions
 * - Field mappings (frontend <-> backend)
 * - Calculations
 * - API endpoints
 * - Permissions
 * - Available features
 */

import { isFieldRunShop } from '../../utils/pipingFieldRun';

/**
 * Field mapping from frontend camelCase to backend snake_case
 */
export const FIELD_MAP = {
  isoSheetNumber: 'iso_sheet_number',
  fluidCode: 'fluid_code',
  testPackage1: 'test_package_1',
  testPackage2: 'test_package_2',
  pAndId: 'pnid',
  udfType: 'udf_type',
  size1: 'size_1',
  material1: 'material_1',
  schedule1: 'schedule_1',
  length1: 'length_1',
  fabricationShop: 'fabrication_shop',
  totalShopCompletion: 'total_shop_completion',
  priority: 'priority',
  fitUp: 'fit_up',
  received: 'received',
  loadNumber: 'load_number',
  dateShipped: 'date_shipped',
  boltUpWeld: 'bolt_up_weld',
  insulation: 'insulation',
  heatTracing: 'heat_tracing',
  transportToInstallationArea: 'transport_to_installation_area',
  erectSupportsHangers: 'erect_supports_hangers',
  lineCleanupHydroTest: 'line_cleanup_hydro_test',
  comments: 'comments',
  nde: 'nde',
  ndeType: 'nde_type',
  activityId: 'activity_id',
  activityName: 'activity_name',
  fabActivityId: 'fab_activity_id',
  fabActivityName: 'fab_activity_name',
  p6ProjectId: 'p6_project_id',
  udf01: 'udf01',
  udf02: 'udf02',
  udf03: 'udf03',
  udf04: 'udf04',
  udf05: 'udf05',
  udf06: 'udf06',
  udf07: 'udf07',
  udf08: 'udf08',
  udf09: 'udf09',
  udf10: 'udf10',
  onHold: 'on_hold',
};

/** Row-level sub-disciplines for the Piping tracker filter dropdown */
export const PIPING_SUB_DISCIPLINES = [
  { value: 'Piping', label: 'Piping' },
  { value: 'Tubing', label: 'Tubing' },
  { value: 'Supports', label: 'Supports' },
];

/**
 * Columns that affect the total_field_completion calculation
 * Note: field_total_completion is a database GENERATED column - these are for UI indication only
 * Database formula: (transport * w_transport) + (erect * w_supports) + (fitUp * w_fitup) + (boltWeld * w_boltweld) + (lineCleanup * w_cleanup)
 * Default weights: 0.15, 0.20, 0.10, 0.35, 0.20
 */
export const FIELD_COMPLETION_COLUMNS = [
  'transportToInstallationArea',
  'erectSupportsHangers',
  'fitUp',
  'boltUpWeld',
  'lineCleanupHydroTest',
];

/**
 * Editable columns for progress tracking
 */
export const EDITABLE_COLUMNS = [
  'activityId',
  'activityName',
  'fabricationShop',
  'totalShopCompletion',
  'priority',
  'loadNumber',
  'dateShipped',
  'received',
  'transportToInstallationArea',
  'erectSupportsHangers',
  'fitUp',
  'boltUpWeld',
  'lineCleanupHydroTest',
  'heatTracing',
  'insulation',
  'comments',
  'nde',
  'ndeType',
  'udfType',
  'testPackage1',
  'testPackage2',
  'udf01', 'udf02', 'udf03', 'udf04', 'udf05',
  'udf06', 'udf07', 'udf08', 'udf09', 'udf10',
  'onHold',
];

/**
 * @deprecated This function is NOT used - field_total_completion is a database GENERATED column
 * The database automatically calculates this using the w_* weight columns on each row.
 * Kept for reference only. See PIPING_PROGRESS_TRACKING_ANALYSIS.md for details.
 * 
 * Database formula (with default weights):
 * (transport * 0.15) + (erect * 0.20) + (fitUp * 0.10) + (boltWeld * 0.35) + (lineCleanup * 0.20)
 */
export const calculateFieldTotalCompletion = (row) => {
  console.warn('calculateFieldTotalCompletion is deprecated - field_total_completion is calculated by the database');
  const transport = Number(row.transportToInstallationArea) || 0;
  const erect = Number(row.erectSupportsHangers) || 0;
  const fitUp = Number(row.fitUp) || 0;
  const boltUp = Number(row.boltUpWeld) || 0;
  const lineCleanup = Number(row.lineCleanupHydroTest) || 0;
  
  // Using default database weights
  const total = (transport * 0.15) + (erect * 0.20) + (fitUp * 0.10) + (boltUp * 0.35) + (lineCleanup * 0.20);
  return Math.round(total * 100) / 100;
};

/**
 * Transform raw API data to frontend format
 */
export const transformMatrixData = (matrixData) => {
  return matrixData.map((row, index) => {
    const transformed = {
      id: row.id || index,
      isoSheetNumber: row.iso_sheet_number || '',
      fluidCode: row.fluid_code || '',
      testPackage1: row.test_package_1 || '',
      testPackage2: row.test_package_2 || '',
      pAndId: row.pnid || '',
      udfType: row.udf_type || '',
      priority: row.priority || null,
      priorityId: row.priority || null,
      priorityName: row.priority_name || '',
      priorityColor: row.priority_color || '',
      size1: row.size_1 || '',
      material1: row.material_1 || '',
      schedule1: row.schedule_1 || '',
      length1: row.length_1 ? `${row.length_1}` : '',
      fabricationShop: row.fabrication_shop || '',
      totalShopCompletion: row.total_shop_completion ? Math.round(row.total_shop_completion) : 0,
      loadNumber: row.load_number || '',
      dateShipped: row.date_shipped || '',
      received: row.received ? Math.round(row.received) : 0,
      transportToInstallationArea: row.transport_to_installation_area ? Math.round(row.transport_to_installation_area) : 0,
      erectSupportsHangers: row.erect_supports_hangers ? Math.round(row.erect_supports_hangers) : 0,
      fitUp: row.fit_up ? Math.round(row.fit_up) : 0,
      boltUpWeld: row.bolt_up_weld ? Math.round(row.bolt_up_weld) : 0,
      lineCleanupHydroTest: row.line_cleanup_hydro_test ? Math.round(row.line_cleanup_hydro_test) : 0,
      heatTracing: row.heat_tracing ? Math.round(row.heat_tracing) : 0,
      insulation: row.insulation ? Math.round(row.insulation) : 0,
      totalFieldCompletion: row.total_field_completion ? Math.round(row.total_field_completion) : 0,
      sovCode: row.sov || '',
      sovDescription: row.sov_description || '',
      budgetedHr: row.budget_hours ? Number(row.budget_hours) : 0,
      credit: row.budget_credit ? Number(row.budget_credit) : 0,
      earnedHr: (Number(row.earned_hours_shop) || 0) + (Number(row.earned_hours_field) || 0),
      earnedCredit: (Number(row.earned_credit_shop) || 0) + (Number(row.earned_credit_field) || 0),
      earnedCreditShop: isFieldRunShop(row.fabrication_shop) ? 0 : (Number(row.earned_credit_shop) || 0),
      earnedCreditField: Number(row.earned_credit_field) || 0,
      comments: row.comments || '',
      nde: row.nde || '',
      ndeType: row.nde_type || '',
      activityId: row.activity_id || '',
      activityName: row.activity_name || '',
      fabActivityId: row.fab_activity_id || '',
      fabActivityName: row.fab_activity_name || '',
      p6ProjectId: row.p6_project_id || '',
      onHold: row.on_hold || false,
      holdUpdatedAt: row.hold_updated_at || null,
      holdUpdatedBy: row.hold_updated_by || '',
      holdReason: row.hold_reason || '',
      // Plan 11: MTO version line model
      isoVersion: row.iso_version || '',
      mtoRevNo: (row.mto_rev_no === null || row.mto_rev_no === undefined) ? '' : row.mto_rev_no,
      isSuperseded: row.is_superseded === true,
      supersededAt: row.superseded_at || null,
      baselineOrExtra: row.baseline_or_extra || '',
    };
    
    // Add UDF columns (they are mapped by column mappings)
    for (let i = 1; i <= 10; i++) {
      const udfKey = `udf${String(i).padStart(2, '0')}`;
      transformed[udfKey] = row[udfKey] || '';
    }
    
    return transformed;
  });
};

/**
 * Build change payload for saving a cell change
 */
export const buildChangePayload = (rowId, columnId, oldValue, newValue) => {
  const backendField = FIELD_MAP[columnId];
  if (!backendField) return null;

  const payload = {
    id: rowId,
    changes: {},
  };

  if (backendField === 'total_shop_completion') {
    payload.changes[backendField] = {
      old_value: oldValue,
      new_value: newValue,
    };
  } else {
    payload.changes[backendField] = newValue;
  }

  return payload;
};

/**
 * Piping discipline configuration
 */
export const pipingConfig = {
  id: 'piping',
  name: 'Piping & Insulation',
  menuId: 'piping-insulation', // Maps to ProjectMenu item ID
  
  // API endpoints (use {projectId} as placeholder)
  api: {
    getData: '/matrix/project/{projectId}',
    saveChanges: '/matrix/save-changes',
    export: '/matrix/export/{projectId}',
    upload: '/matrix/upload',
    priorities: '/priorities',  // Global priorities endpoint (no projectId)
    columnMappings: '/matrix/project/{projectId}/column-mappings',
    progressHistory: '/matrix/{matrixId}/progress-history',
    holdList: '/matrix/project/{projectId}/hold-list',
  },
  
  // Permission requirements (pt_* = Progress Tracking module)
  permissions: {
    edit: 'pt_edit',
    editAll: 'pt_edit_all',
    analytics: 'pt_analytics',
    import: 'pt_import',
    export: 'pt_export',
    template: 'pt_template',
    columnConfig: 'pt_columnconfig',
    roc: 'pt_roc',
    sfRatio: 'pt_sfratio',
    matBore: 'pt_matbore',
    delete: 'pt_delete',
  },
  
  // Column definitions
  columns: [
    // 1. ISO Sheet Number - clickable link to progress trends
    {
      id: 'isoSheetNumber',
      header: 'ISO Sheet Number',
      accessor: 'isoSheetNumber',
      dbField: 'iso_sheet_number',
      width: 140,
      editable: false,
      filterable: true,
      type: 'link',
    },
    // 1b. Hold - checkbox to flag ISO as on hold
    {
      id: 'onHold',
      header: 'Hold',
      accessor: 'onHold',
      dbField: 'on_hold',
      width: 50,
      editable: true,
      filterable: true,
      type: 'hold-checkbox',
    },
    // Schedule link — rolls into Schedule Instances % when set
    {
      id: 'activityId',
      header: 'Activity ID',
      accessor: 'activityId',
      dbField: 'activity_id',
      width: 140,
      editable: true,
      filterable: true,
      type: 'text',
      showInDashboard: true,
      headerGroup: 'Schedule',
    },
    {
      id: 'activityName',
      header: 'Activity Name',
      accessor: 'activityName',
      dbField: 'activity_name',
      width: 180,
      editable: true,
      filterable: true,
      type: 'text',
      showInDashboard: false,
      headerGroup: 'Schedule',
    },
    {
      id: 'fabActivityId',
      header: 'Fab Activity ID',
      accessor: 'fabActivityId',
      dbField: 'fab_activity_id',
      width: 140,
      editable: true,
      filterable: true,
      type: 'text',
      showInDashboard: true,
      headerGroup: 'Schedule',
    },
    {
      id: 'fabActivityName',
      header: 'Fab Activity Name',
      accessor: 'fabActivityName',
      dbField: 'fab_activity_name',
      width: 180,
      editable: true,
      filterable: true,
      type: 'text',
      showInDashboard: false,
      headerGroup: 'Schedule',
    },
    // 1c. ISO Version (MTO Plan 11) - read-only; superseded lines render struck-through
    {
      id: 'isoVersion',
      header: 'ISO Ver',
      accessor: 'isoVersion',
      dbField: 'iso_version',
      width: 64,
      editable: false,
      filterable: true,
      type: 'text',
    },
    // 1d. MTO Rev # (MTO Plan 11) - read-only; the MTO revision that produced this line
    {
      id: 'mtoRevNo',
      header: 'MTO Rev',
      accessor: 'mtoRevNo',
      dbField: 'mto_rev_no',
      width: 64,
      editable: false,
      filterable: true,
      type: 'text',
    },
    // 1e. Sub-discipline (Piping / Tubing / Supports) — seeded into udf_type
    {
      id: 'udfType',
      header: 'Sub-discipline',
      accessor: 'udfType',
      dbField: 'udf_type',
      width: 100,
      editable: true,
      filterable: true,
      type: 'text',
    },
    // 2. Fluid Code
    {
      id: 'fluidCode',
      header: 'Fluid Code',
      accessor: 'fluidCode',
      dbField: 'fluid_code',
      width: 70,
      editable: false,
      restrictedEdit: true,
      filterable: true,
      type: 'text',
    },
    // 2b. Test Package Numbers (max 2 per ISO)
    {
      id: 'testPackage1',
      header: 'Test Package 1',
      accessor: 'testPackage1',
      dbField: 'test_package_1',
      width: 110,
      editable: true,
      filterable: true,
      type: 'text',
    },
    {
      id: 'testPackage2',
      header: 'Test Package 2',
      accessor: 'testPackage2',
      dbField: 'test_package_2',
      width: 110,
      editable: true,
      filterable: true,
      type: 'text',
    },
    // 3. P&ID
    {
      id: 'pAndId',
      header: 'P&ID',
      accessor: 'pAndId',
      dbField: 'pnid',
      width: 120,
      editable: false,
      restrictedEdit: true,
      filterable: true,
      type: 'text',
    },
    // 4. Priority
    {
      id: 'priority',
      header: 'Priority',
      accessor: 'priorityName',
      dbField: 'priority',
      width: 110,
      editable: true,
      filterable: true,
      type: 'priority',
    },
    // 5. Size 1
    {
      id: 'size1',
      header: 'Size 1',
      accessor: 'size1',
      dbField: 'size_1',
      width: 55,
      editable: false,
      restrictedEdit: true,
      filterable: true,
      type: 'text',
    },
    // 6. Material 1
    {
      id: 'material1',
      header: 'Material 1',
      accessor: 'material1',
      dbField: 'material_1',
      width: 80,
      editable: false,
      restrictedEdit: true,
      filterable: true,
      type: 'text',
    },
    // 7. Schedule
    {
      id: 'schedule1',
      header: 'Schedule',
      accessor: 'schedule1',
      dbField: 'schedule_1',
      width: 70,
      editable: false,
      restrictedEdit: true,
      filterable: true,
      type: 'text',
    },
    // 8. Length 1
    {
      id: 'length1',
      header: 'Length 1',
      accessor: 'length1',
      dbField: 'length_1',
      width: 70,
      editable: false,
      restrictedEdit: true,
      filterable: true,
      type: 'text',
    },
    // 9. Fabrication Shop (dropdown from fabrication_locations table)
    {
      id: 'fabricationShop',
      header: 'Fabrication Shop',
      accessor: 'fabricationShop',
      dbField: 'fabrication_shop',
      width: 150,
      editable: true,
      filterable: true,
      type: 'fabrication-shop',  // Special type for dropdown from fabrication_locations
      headerColor: '#FFF3E0',
      headerGroup: 'Shop',
    },
    // 10. Total Shop Completion
    {
      id: 'totalShopCompletion',
      header: 'Total Shop Completion',
      accessor: 'totalShopCompletion',
      dbField: 'total_shop_completion',
      width: 85,
      editable: true,
      filterable: true,
      type: 'progress',
      // Updated 2026-04-18 from [0, 25, 50, 75, 100] to align with new shop progress increments.
      // Updated 2026-06-20: added 40 ("Issued to Bench") to track ISOs handed to the bench between Boxed & Cut (35) and Tacked (65).
      // Existing rows with 25/50/75 are preserved (no backend or DB validation enforces this set).
      options: [0, 35, 40, 65, 95, 100],
      headerColor: '#FFF3E0',
      headerGroup: 'Shop',
    },
    // 11. Load Number
    {
      id: 'loadNumber',
      header: 'Load #',
      accessor: 'loadNumber',
      dbField: 'load_number',
      width: 120,
      editable: true,
      filterable: true,
      type: 'text',
      headerColor: '#FFF3E0',
      headerGroup: 'Shop',
    },
    // 12. Date Shipped
    {
      id: 'dateShipped',
      header: 'Date Shipped',
      accessor: 'dateShipped',
      dbField: 'date_shipped',
      width: 100,
      editable: true,
      filterable: true,
      type: 'date',
      headerColor: '#FFF3E0',
      headerGroup: 'Shop',
    },
    // 13. Received
    {
      id: 'received',
      header: 'Received',
      accessor: 'received',
      dbField: 'received',
      width: 70,
      editable: true,
      filterable: true,
      type: 'progress',
      options: [0, 100],
      headerColor: '#E3F2FD',
      headerGroup: 'Field',
    },
    // 14. Transport To Installation Area
    {
      id: 'transportToInstallationArea',
      header: 'Transport To Installation Area',
      accessor: 'transportToInstallationArea',
      dbField: 'transport_to_installation_area',
      width: 95,
      editable: true,
      filterable: true,
      type: 'progress',
      options: [0, 25, 50, 75, 100],
      affectsCalculation: 'totalFieldCompletion',
      headerColor: '#E3F2FD',
      headerGroup: 'Field',
    },
    // 15. Erect Supports Hangers
    {
      id: 'erectSupportsHangers',
      header: 'Erect Supports / Hangers',
      accessor: 'erectSupportsHangers',
      dbField: 'erect_supports_hangers',
      width: 85,
      editable: true,
      filterable: true,
      type: 'progress',
      options: [0, 25, 50, 75, 100],
      affectsCalculation: 'totalFieldCompletion',
      headerColor: '#E3F2FD',
      headerGroup: 'Field',
    },
    // 16. Fit Up
    {
      id: 'fitUp',
      header: 'Fit Up',
      accessor: 'fitUp',
      dbField: 'fit_up',
      width: 60,
      editable: true,
      filterable: true,
      type: 'progress',
      options: [0, 25, 50, 75, 100],
      affectsCalculation: 'totalFieldCompletion',
      headerColor: '#E3F2FD',
      headerGroup: 'Field',
    },
    // 17. Bolt/Weld
    {
      id: 'boltUpWeld',
      header: 'Bolt / Weld',
      accessor: 'boltUpWeld',
      dbField: 'bolt_up_weld',
      width: 65,
      editable: true,
      filterable: true,
      type: 'progress',
      options: [0, 25, 50, 75, 100],
      affectsCalculation: 'totalFieldCompletion',
      headerColor: '#E3F2FD',
      headerGroup: 'Field',
    },
    // 18. Line Cleanup / Hydro Test
    {
      id: 'lineCleanupHydroTest',
      header: 'Line Cleanup / Hydro Test',
      accessor: 'lineCleanupHydroTest',
      dbField: 'line_cleanup_hydro_test',
      width: 90,
      editable: true,
      filterable: true,
      type: 'progress',
      options: [0, 25, 50, 75, 100],
      affectsCalculation: 'totalFieldCompletion',
      headerColor: '#E3F2FD',
      headerGroup: 'Field',
    },
    // 19. Heat Tracing
    {
      id: 'heatTracing',
      header: 'Heat Tracing',
      accessor: 'heatTracing',
      dbField: 'heat_tracing',
      width: 70,
      editable: true,
      filterable: true,
      type: 'progress',
      options: [0, 25, 50, 75, 100],
      headerColor: '#E3F2FD',
      headerGroup: 'Field',
    },
    // 20. Insulation
    {
      id: 'insulation',
      header: 'Insulation',
      accessor: 'insulation',
      dbField: 'insulation',
      width: 70,
      editable: true,
      filterable: true,
      type: 'progress',
      options: [0, 25, 50, 75, 100],
      headerColor: '#E3F2FD',
      headerGroup: 'Field',
    },
    // Note: Budget/Earned Hours Insulation and Heat Tracing columns exist in DB but are hidden from UI
    // They can be re-enabled by uncommenting the column definitions below if needed
    
    // 21. Total Field Completion (computed, read-only)
    {
      id: 'totalFieldCompletion',
      header: 'Total Field Completion',
      accessor: 'totalFieldCompletion',
      dbField: 'total_field_completion',
      width: 85,
      editable: false,
      filterable: true,
      type: 'progress-readonly',
      computed: true,
      headerColor: '#E3F2FD',
      headerGroup: 'Field',
    },
    // Note: Budget Hours, Credit, Earned Hours, Earned Credit columns hidden from matrix UI
    // Data still used internally by analytics (Service tab, etc.)
    // To re-enable, uncomment the column definitions below:
    // { id: 'budgetedHr', header: 'Budget Hours', accessor: 'budgetedHr', dbField: 'budget_hours', width: 85, editable: false, filterable: true, type: 'number', headerColor: '#FFF9C4', headerGroup: 'Budget & Earned' },
    // { id: 'credit', header: 'Credit', accessor: 'credit', dbField: 'budget_credit', width: 75, editable: false, filterable: true, type: 'number', computed: true, headerColor: '#FFF9C4', headerGroup: 'Budget & Earned' },
    // { id: 'earnedHr', header: 'Earned Hours', accessor: 'earnedHr', dbField: 'earned_hours', width: 85, editable: false, filterable: true, type: 'number', computed: true, headerColor: '#FFF9C4', headerGroup: 'Budget & Earned' },
    // { id: 'earnedCredit', header: 'Earned Credit', accessor: 'earnedCredit', dbField: 'earned_credit', width: 85, editable: false, filterable: true, type: 'number', computed: true, headerColor: '#FFF9C4', headerGroup: 'Budget & Earned' },
    // 26. Comments
    {
      id: 'comments',
      header: 'Comments',
      accessor: 'comments',
      dbField: 'comments',
      width: 200,
      editable: true,
      filterable: true,
      type: 'text',
    },
    // 27. NDE (Y/N dropdown) - Hidden from dashboard; tracked in NDE Tracker
    {
      id: 'nde',
      header: 'NDE',
      accessor: 'nde',
      dbField: 'nde',
      width: 55,
      editable: true,
      filterable: true,
      type: 'select',
      options: ['', 'Y', 'N'],
      headerColor: '#F3E8FF',
      headerGroup: 'NDE',
      showInDashboard: false,
    },
    // 28. NDE Type (dropdown) - Hidden from dashboard; tracked in NDE Tracker
    {
      id: 'ndeType',
      header: 'NDE Type',
      accessor: 'ndeType',
      dbField: 'nde_type',
      width: 90,
      editable: true,
      filterable: true,
      type: 'select',
      options: ['', 'X-Ray', 'LPI', 'MPI', 'UT', 'VT', 'PMI'],
      headerColor: '#F3E8FF',
      headerGroup: 'NDE',
      showInDashboard: false,
    },
  ],
  
  // UDF (User Defined Fields) columns - added dynamically based on project config
  udfColumns: Array.from({ length: 10 }, (_, i) => ({
    id: `udf${String(i + 1).padStart(2, '0')}`,
    header: `UDF ${String(i + 1).padStart(2, '0')}`,
    accessor: `udf${String(i + 1).padStart(2, '0')}`,
    dbField: `udf${String(i + 1).padStart(2, '0')}`,
    width: 120,
    editable: true,
    filterable: true,
    type: 'text',
    isUdf: true,
  })),
  
  // Calculated fields
  calculations: {
    totalFieldCompletion: {
      dependencies: FIELD_COMPLETION_COLUMNS,
      formula: calculateFieldTotalCompletion,
    },
  },
  
  // Field mappings
  fieldMap: FIELD_MAP,
  editableColumns: EDITABLE_COLUMNS,
  
  // Data transformation
  transformData: transformMatrixData,
  buildChangePayload: buildChangePayload,
  
  // Available features for this discipline
  features: {
    dragFill: true,
    progressTrends: true,
    matBore: true,
    rulesOfCredit: true,
    columnConfig: true,
    actualSpentUpload: true,
    export: true,
    upload: true,
    presence: true, // Real-time presence tracking (shows who's viewing)
  },
};

export default pipingConfig;
